import { isSuccess, type Result } from '@carvana/merch-result';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';
import { httpWithAuth } from '~/clients';
import { FinancingApiClientId } from '~/constants/common/ApiClientId';
import { getFinancingTerms } from '~/constants/endpoints';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { CreditApplication, PricingAggregates } from '~/contracts/flp';
import showApiError from '~/utilities/flp/showApiError';
import { logError } from '~/utilities/loggers';
import toError from '~/utilities/toError';

export default function useRefreshTerms() {
  const eventCorrelationId = useEventCorrelationId();
  const { tradeIn, setIsInitialized, setIsLoading, setRefreshedData } =
    useFinancingContext();

  return useCallback(
    async (applyTradeIn?: boolean) => {
      setIsLoading(true);
      datadogRum.addAction(DATADOG_EVENTS.API.REFRESH_TERMS_API_CALL);

      try {
        const shouldApplyTradeIn =
          applyTradeIn ?? Boolean(tradeIn?.data?.applyTradeIn);

        const response = await httpWithAuth.post<
          Result<{
            creditApplication: Result<CreditApplication | null>;
            pricingAggregates?: Result<PricingAggregates>;
          }>
        >(
          getFinancingTerms(shouldApplyTradeIn, {
            currentPageUrl: window.location.href
          }),
          {},
          {
            headers: {
              'x-client-id': FinancingApiClientId,
              'x-correlation-id': eventCorrelationId
            }
          }
        );

        if (!isSuccess(response.data)) {
          datadogRum.addAction(
            DATADOG_EVENTS.API.REFRESH_TERMS_INVALID_RESPONSE,
            { error: response.data.errorMessage }
          );

          throw new Error(
            'Incomplete data received from server. Please try again.'
          );
        }

        const { creditApplication, pricingAggregates } = response.data.content;

        // customer does not have a valid softpull, no need to set any values.
        if (!isSuccess(creditApplication) || !creditApplication.content) {
          return;
        }

        setRefreshedData(
          'Refresh Terms',
          shouldApplyTradeIn,
          creditApplication.content,
          isSuccess(pricingAggregates) ? pricingAggregates.content : undefined
        );
      } catch (err) {
        datadogRum.addAction(DATADOG_EVENTS.API.REFRESH_TERMS_API_ERROR, {
          error: err instanceof Error ? err.message : String(err)
        });
        logError(err, { location: 'Refresh Terms' });

        showApiError();

        // throw the error again so the consumer can handle it
        throw toError(err);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    },
    [
      eventCorrelationId,
      setIsInitialized,
      setIsLoading,
      setRefreshedData,
      tradeIn?.data?.applyTradeIn
    ]
  );
}
